import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Space, Table, message, Breadcrumb } from 'antd';
import { addNewSearch } from 'utils/utils';
import { CardLayout, TableWrapper, UserResetPasswordModal, UserEditModal } from 'components/molecules';
import qs from 'query-string';
import { ColumnsType } from 'antd/lib/table/interface';
import { useTableInfo, useUser } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import UserSearchBox from './SearchBox'
import { TotalNumMenu } from 'components/atoms';
import { usePopupManager } from 'react-popup-manager';
import { SERVER_URL } from 'commons/constants';
import axios from 'axios';
import { endpoints } from 'services';
import moment from 'moment';

export interface UserProps {}

const User = ({}: UserProps): React.ReactElement => {
  /* ======================================================
   * hooks
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { userList, searchResult, userUpdate, userInsert, resetPassword } = useUser();  

  /* ======================================================
   * variables
   * ====================================================== */
  const { open } = usePopupManager();

  const searchParams = qs.parse(location.search);
  const columns: ColumnsType<UserListDto> = [
    {
      title: '번호',
      dataIndex: 'id',
      key: 'id',
      width: '70px',
      align: 'center',
    },  
    {
      title: '이름',
      dataIndex: 'username',
      key: 'username',
      width: '120px',
      align: 'center',
    },
    {
      title: '이메일',
      dataIndex: 'email',
      key: 'email',
      width: 'auto',
      align: 'center',
    },
    {
      title: '역할',
      dataIndex: 'position',
      key: 'position',
      width: '120px',
      align: 'center',
      render: (text, record) => {
        const position : string[] = [];
        if (record.isCm) position.push('CM');
        if (record.isPm) position.push('PM');
        if (record.isOp) position.push('운영자');
        return (
          position.join(', ')
        )
      }      
    },
    {
      title: '최종 로그인 일시',
      dataIndex: 'last_login',
      key: 'last_login',
      width: '150px',
      align: 'center',
    },
    {
      title: '최종 로그인 IP',
      dataIndex: 'last_ip',
      key: 'last_ip',
      width: '110px',
      align: 'center',
    },
    {
      title: '생성일',
      dataIndex: 'created',
      key: 'created',
      width: '150px',
      align: 'center',
    },
    {
      title: '수정일',
      dataIndex: 'modified',
      key: 'modified',
      width: '150px',
      align: 'center',
    },
    {
      title: '사용여부',
      dataIndex: 'loginYn',
      key: 'loginYn',
      width: '80px',
      align: 'center',
      render: (loginYn, record) => {
        return (
          <Button
            type='default'
            onClick={(e) => {
              e.stopPropagation();

              userUpdate({
                id: record.id,
                loginYn: loginYn === 'Y' ? 'N' : 'Y',
              })              
            }}
          >{loginYn === 'Y' ? 'YES' : 'NO'}</Button>
        )
      }
    },
    {
      title: '관리',
      key: 'chgangePass',
      width: '115px',
      align: 'center',
      render: (record) => {
        return (
          <Button
            type='default'
            onClick={(e) => {
              e.stopPropagation();
              open(UserResetPasswordModal, {
                username: String(record.username),
                onClose: (isOk, newpassword) => {
                  if (isOk) {
                    resetPassword({
                      id: record.id,
                      password: newpassword,
                    }) 
                  }
                },
              });              
            }}
          >비밀번호변경</Button>
        )
      }
    },    
  ];

  /* ======================================================
   * events
   * ====================================================== */    
  const onClickRegister = () => {
    open(UserEditModal, {
      id: 0,
      onClose: (isOk, username, email, isOp, isCm, isPm, password, userDiv) => {
        if (isOk) {
          userInsert({
            id: 0,
            username: username,
            email: email,
            isOp: isOp,
            isCm: isCm, 
            isPm: isPm,
            password: password,  
            userDiv: userDiv,
          })               
        }
      },
    }); 
  }

  const onChangePageInfo = (pageNo: number, pageSize: number) => {
    navigate({
      ...location,
      search: addNewSearch(location.search, { pageNo, pageSize }),
    });
  };  
  const onRowClick = useCallback((record, index) => {
    return {
      onClick: () => {
        open(UserEditModal, {
          id: Number(record.id),
          username: String(record.username),
          email: String(record.email),
          isOp: Number(record.isOp),
          isCm: Number(record.isCm),
          isPm: Number(record.isPm),
          userDiv: record.userDiv,
          onClose: (isOk, username, email, isOp, isCm, isPm, password, banned, ban_reason, userDiv) => {
            if (isOk) {
              userUpdate({
                id: record.id,
                username: username,
                isOp: isOp,
                isCm: isCm, 
                isPm: isPm,
                password: password,                
                banned: banned,
                ban_reason: ban_reason,
                userDiv: userDiv,
              })               
            }
          },
        }); 
      },
    };
  }, []);


  const onExcelDownload = () => {
    axios({
      url: SERVER_URL + endpoints.user.downloadexcel,
      method: 'post',
      responseType: 'arraybuffer',
      params: searchParams,
    })
    .then((response) => {
      const fileName = "사용자정보_" + moment().format('YYYYMMDD_HHmmss')+".xlsx";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}`);
      link.style.cssText = 'display:none';
      document.body.appendChild(link);
      link.click();

    })
    .catch(() => {
      message.error('파일 다운로드 중 에러가 발생했습니다.', 1);
    });   

  }  

  /* ======================================================
   * hooks
   * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: userList,
    totalCount: searchResult.totalCount,
    onChangePageInfo,
  });

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <CardLayout title={(
      <Breadcrumb>
        <Breadcrumb.Item>Home</Breadcrumb.Item>
        <Breadcrumb.Item>사용자관리</Breadcrumb.Item>
    </Breadcrumb>      
    )}>
        <Space direction="vertical" style={{ width: '100%' }}>
            <UserSearchBox />
            <TotalNumMenu totalNum={searchResult.totalCount} useRegister useExcelDownload onUseRegister={onClickRegister} onDownloadExcel={onExcelDownload}/>
            <TableWrapper isSearch={!!searchParams.search || !!searchParams.use || !!searchParams.position}>
              <Table
                tableLayout="fixed"
                dataSource={userList}
                columns={columns}
                pagination={pagination}
                onChange={onTableChange}
                onRow={onRowClick}
                style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
                className="columnCenterFixTable clickable"
                showSorterTooltip={false}
                rowKey={'id'}
              />
            </TableWrapper>                
        </Space>                
    </CardLayout>
  )
};

export default User;    